// src/pages/VideoStreams/videoStreamsApi.js
// Слой доступа к данным модуля «Видеопоток» (АРМ). Без внешних зависимостей — fetch.
// Единый ресурс с основным модулем (таблица video_streams).
//   GET  /api/video-streams        → массив записей            (RQ.21.01)
//   POST /api/video-streams        → 201 + созданная запись    (RQ.21.03, multipart/form-data)
// Коды ошибок, которые обрабатывает форма: 422 (body.fields), 413, 415, 500/сеть.

const API_BASE = process.env.REACT_APP_API_BASE || ''; // напр. https://sc.gov.kz
const ENDPOINT = '/api/video-streams';
// eslint-disable-next-line no-undef

const TEST_VIDEO_URL = (process.env.PUBLIC_URL || '') + '/mock/sample-5s-720p.mp4';

// В проде оставить false — пойдут реальные запросы к ENDPOINT.
const USE_MOCK = true;

export class ApiError extends Error {
  constructor(status, body) {
    super('api_error');
    this.status = status;
    this.body = body;
  }
}

/* ----------------------------- Реальный бэкенд ---------------------------- */
const httpApi = {
  async list() {
    const res = await fetch(API_BASE + ENDPOINT, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new ApiError(res.status);
    const data = await res.json();
    return Array.isArray(data) ? data : data.items || [];
  },
  async create(formData) {
    const res = await fetch(API_BASE + ENDPOINT, { method: 'POST', body: formData });
    if (res.status === 201) return res.json(); // тело ответа = созданная запись
    let body = null;
    try {
      body = await res.json();
    } catch (e) {
      /* тело может быть пустым */
    }
    throw new ApiError(res.status, body);
  },
};

/* ------------------------------- Демо-режим ------------------------------- */
const mockApi = (() => {
  let seq = 14;
  const rec = (id, source_name, address, location, file_name, plan_date, fact_date) => ({
    id,
    source_name,
    address,
    location,
    video: { file_name, url: TEST_VIDEO_URL },
    plan_date,
    fact_date,
  });
  let store = [
    rec(
      1,
      'Обзорная камера',
      'ул. Сатпаева, 12',
      'Центральная площадь',
      'stream-01.mp4',
      '2025-03-31',
      '2025-03-24'
    ),
    rec(
      2,
      'Купольная камера PTZ',
      'ул. Сатпаева, 12',
      'Центральная площадь',
      'stream-02.mp4',
      '2025-03-31',
      '2025-04-18'
    ),
    rec(
      3,
      'Обзорная камера',
      'ул. Абая, 45',
      'Здание акимата района',
      'stream-03.mp4',
      '2025-06-30',
      '2025-06-12'
    ),
    rec(
      4,
      'Камера распознавания ГРНЗ',
      'трасса Атырау–Кульсары, 12 км',
      'Южный въезд в город',
      'stream-04.mp4',
      '2025-06-30',
      '2025-07-15'
    ),
    rec(
      5,
      'Камера фиксации нарушений ПДД',
      'трасса Атырау–Кульсары, 12 км',
      'Южный въезд в город',
      'stream-05.mp4',
      '2025-09-30',
      '2025-09-26'
    ),
    rec(
      6,
      'Обзорная камера',
      'мкр. Береке, 3',
      'Рынок «Береке»',
      'stream-06.mp4',
      '2025-09-30',
      '2025-10-08'
    ),
    rec(
      7,
      'Купольная камера PTZ',
      'мкр. Береке, 3',
      'Рынок «Береке»',
      'stream-07.mp4',
      '2025-12-31',
      null
    ),
    rec(
      8,
      'Обзорная камера',
      'пр. Достык, 8',
      'Городской парк',
      'stream-08.mp4',
      '2025-12-31',
      '2025-12-19'
    ),
    rec(
      9,
      'Камера фиксации нарушений ПДД',
      'пр. Достык, 8',
      'Городской парк',
      'stream-09.mp4',
      null,
      '2026-01-23'
    ),
    rec(
      10,
      'Обзорная камера',
      'ул. Жамбыла, 21',
      'Автовокзал',
      'stream-10.mp4',
      '2026-03-31',
      '2026-03-11'
    ),
    rec(
      11,
      'Камера распознавания ГРНЗ',
      'ул. Жамбыла, 21',
      'Автовокзал',
      'stream-11.mp4',
      '2026-03-31',
      null
    ),
    rec(
      12,
      'Купольная камера PTZ',
      'ул. Абая, 45',
      'Стадион',
      'stream-12.mp4',
      '2026-06-30',
      '2026-06-05'
    ),
    rec(13, 'Обзорная камера', 'ул. Абая, 45', 'Школа №1', 'stream-13.mp4', '2026-06-30', null),
    rec(
      14,
      'Камера фиксации нарушений ПДД',
      'ул. Сатпаева, 12',
      'Школа №1',
      'stream-14.mp4',
      '2026-09-30',
      null
    ),
  ];
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  return {
    async list() {
      await wait(500);
      return store.slice();
    },
    async create(formData) {
      await wait(600);
      const g = (k) => (formData.get(k) || '').toString().trim();
      const file = formData.get('video');
      const fields = {};
      if (!g('source_name')) fields.source_name = 'Поле обязательно для заполнения';
      if (!g('address')) fields.address = 'Поле обязательно для заполнения';
      if (!g('location')) fields.location = 'Поле обязательно для заполнения';
      if (!file || !file.name) fields.video = 'Прикрепите видеофайл';
      if (Object.keys(fields).length) throw new ApiError(422, { fields });
      const created = rec(
        ++seq,
        g('source_name'),
        g('address'),
        g('location'),
        file.name,
        g('plan_date') || null,
        g('fact_date') || null
      );
      created.video.url = URL.createObjectURL(file);
      store = [created, ...store];
      return created;
    },
  };
})();

const api = USE_MOCK ? mockApi : httpApi;

export const listVideoStreams = () => api.list();
export const createVideoStream = (formData) => api.create(formData);
